import { Link } from 'react-router-dom'
import { LockOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import type { ChipTone } from '../../../components/ui/Chip'
import { EmptyState } from '../../../components/ui/EmptyState'
import { formatDate, formatPriceBand } from '../format'
import type { PartActiveQuote, PartDetail, QuoteState } from '../types'

export interface PartActiveQuotesSectionProps {
  readonly part: PartDetail
  /** UID-45 fallback CTA — kept from the pre-UID-332 simple treatment
   * (navigates to the RFQ list) for when `activeQuotes` is absent. */
  readonly onViewQuotes: () => void
}

const QUOTE_STATE_TONE: Record<QuoteState, ChipTone> = {
  draft: 'neutral',
  quoting: 'info',
  quoted: 'purple',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
}

const QUOTE_STATE_LABEL: Record<QuoteState, string> = {
  draft: 'Draft',
  quoting: 'Quoting',
  quoted: 'Quoted',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
}

/** W1 (code-review) — the two terminal `QuoteState`s: a quote in either of
 * these no longer counts as "active" and must not appear in this tab's
 * table, matching the server's `activeQuoteCount` semantics
 * (`types.ts` `PartDetail.activeQuoteCount` doc comment / `schema.gen.ts`). */
const TERMINAL_QUOTE_STATES: ReadonlySet<QuoteState> = new Set(['rejected', 'expired'])

/**
 * Part detail "Active quotes" tab (AC-F4, UID-332/ADR-0113 B3,
 * `dc.html:1812-1838`). Renders the rich `activeQuotes[]` table when present;
 * falls back to the pre-UID-332 count+link treatment when the BFF hasn't
 * seeded `activeQuotes` yet (`activeQuoteCount` never disagrees with the
 * list by construction, per the ADR, so the fallback never contradicts it).
 *
 * W1 (code-review) — `activeQuotes` MAY include terminal (`rejected`/
 * `expired`) entries (e.g. for this tab's own history/"lapsed" context); this
 * component filters those out before rendering so the rendered row count
 * always agrees with `part.activeQuoteCount` (the server's non-terminal
 * count) BY CONSTRUCTION, rather than trusting the caller to have already
 * excluded them.
 */
export function PartActiveQuotesSection({ part, onViewQuotes }: PartActiveQuotesSectionProps) {
  if (part.activeQuotes === undefined) {
    if (part.activeQuoteCount === 0) {
      return <EmptyState title="No active quotes" />
    }
    return (
      <div className="part-detail-quotes-summary">
        <p>
          {part.activeQuoteCount} active quote{part.activeQuoteCount === 1 ? '' : 's'} for this
          part.
        </p>
        <Button variant="outline" size="sm" onClick={onViewQuotes}>
          View quotes
        </Button>
      </div>
    )
  }

  const activeQuotes = part.activeQuotes.filter((quote) => !TERMINAL_QUOTE_STATES.has(quote.status))

  if (activeQuotes.length === 0) {
    return (
      <EmptyState title="No active quotes" detail="Add a revision to a quote to request pricing." />
    )
  }

  return (
    <div className="card part-detail-quotes-table">
      <table className="parts-table part-detail-active-quotes-table">
        <thead>
          <tr>
            <th scope="col">Quote</th>
            <th scope="col">Submitted</th>
            <th scope="col">Revision</th>
            <th scope="col">Qty</th>
            <th scope="col">Pricing</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {activeQuotes.map((quote) => (
            <ActiveQuoteRow key={quote.id} quote={quote} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ActiveQuoteRow({ quote }: { readonly quote: PartActiveQuote }) {
  return (
    <tr>
      <td>
        <Link to={`/rfq/${quote.id}`} className="part-detail-quote-link">
          {quote.id}
        </Link>
        {quote.bundled && (
          <Chip tone="warning" icon={<LockOutlined aria-hidden="true" />}>
            Bundle
          </Chip>
        )}
      </td>
      <td>{formatDate(quote.submittedAt)}</td>
      <td>{quote.revision}</td>
      <td>{quote.quantity}</td>
      <td>{formatPriceBand(quote.pricingRange)}</td>
      <td>
        <Chip tone={QUOTE_STATE_TONE[quote.status]}>{QUOTE_STATE_LABEL[quote.status]}</Chip>
      </td>
    </tr>
  )
}
